<template>
  <div class="mobileViewStyle leave-detail">
    <!-- 请假申请详情：查看模式 / 编辑模式（新建也是编辑模式） -->
    <div class="detail-body">
      <!-- ==================== 查看模式 ==================== -->
      <list-card v-if="isView" :label="detail.leaveReason">
        <template #desc>
          <span class="type-tag" :class="'tag-' + typeTagType(detail.leaveTypeId)">{{ typeName(detail.leaveTypeId) }}</span>
          <span class="status-tag" :class="'tag-' + statusTagType(detail.leaveStatus)">{{ statusName(detail.leaveStatus) }}</span>
          <!-- 只有草稿能改（驳回/中止/撤回后也会回到草稿，同样能改） -->
          <span v-if="isDraft" class="edit-btn" @click="toEdit">{{ editLabel }}</span>
        </template>
        <data-column label="请假单号">{{ detail.leaveNo || "—" }}</data-column>
        <data-column label="申请人">{{ detail.applicantName || "—" }}</data-column>
        <data-column label="所属部门">{{ detail.departTitle || "—" }}</data-column>
        <data-column label="请假类型">{{ typeName(detail.leaveTypeId) || "—" }}</data-column>
        <data-column label="请假时长">{{ durationTypeName(detail.leaveDurationType) || "—" }}</data-column>
        <data-column label="起止时间">{{ dateRangeText(detail.startDate, detail.startTimeType, detail.endDate, detail.endTimeType) }}</data-column>
        <data-column label="请假天数">{{ days(detail.leaveDuration) }} 天</data-column>
        <data-column label="申请日期">{{ dateOnly(detail.applyDate) }}</data-column>
        <data-column label="申请状态">
          <span class="status-tag" :class="'tag-' + statusTagType(detail.leaveStatus)">{{ statusName(detail.leaveStatus) }}</span>
        </data-column>
        <data-column label="审批状态">
          <span class="status-tag" :class="'tag-' + approvalTagType(detail.approvalStatus)">{{ approvalName(detail.approvalStatus) }}</span>
        </data-column>
        <data-column label="提交时间">{{ dateTime(detail.submitTime) }}</data-column>
        <data-column label="审批结束">{{ dateTime(detail.finishTime) }}</data-column>
        <data-column v-if="detail.remark" label="备注">{{ detail.remark }}</data-column>
        <!-- 附件：只读展示，与 web 端同一份 attachUrl -->
        <div v-if="detail.attachUrl" class="attach-block">
          <div class="attach-label">附件</div>
          <mobile-image-upload :model-value="detail.attachUrl" :disabled="true" folder="leave"/>
        </div>
      </list-card>

      <!-- ==================== 编辑模式 ==================== -->
      <list-card v-else label="请假信息" type="form">
        <data-input label="请假类型">
          <mobile-select class="row-select" :clearable="false" v-model="editData.leaveTypeId" placeholder="请选择请假类型">
            <mobile-option v-for="type in typeList" :key="type.userCode" :label="type.dictName" :value="type.userCode"/>
          </mobile-select>
        </data-input>
        <!-- 默认带出当前登录人的主部门，允许改（代他人请假的情况） -->
        <data-input label="所属部门">
          <mobile-select class="row-select" :clearable="false" v-model="editData.departCode"
                         label-column="departName" code-column="departCode" :data="departTree"
                         type="tree" placeholder="请选择部门"/>
        </data-input>
        <!-- 开始时间：日期 + 上午/下午。两者都必填，天数由系统推导 -->
        <data-input label="开始时间">
          <div class="datetime-row">
            <!-- min 只在今天及以后：与原来 disablePast 同一个口径，避免补录历史假单 -->
            <mobile-date-select class="datetime-date" :min="minDate" v-model="editData.startDate"
                                placeholder="开始日期" @change="onTimeChange"/>
            <mobile-select class="row-select time-select" :clearable="false" v-model="editData.startTimeType"
                           placeholder="时段" @change="onTimeChange">
              <mobile-option v-for="t in timeTypes" :key="t.value" :label="t.label" :value="t.value"/>
            </mobile-select>
          </div>
        </data-input>
        <data-input label="结束时间">
          <div class="datetime-row">
            <mobile-date-select class="datetime-date" :min="minDate" v-model="editData.endDate"
                                placeholder="结束日期" @change="onTimeChange"/>
            <mobile-select class="row-select time-select" :clearable="false" v-model="editData.endTimeType"
                           placeholder="时段" @change="onTimeChange">
              <mobile-option v-for="t in timeTypes" :key="t.value" :label="t.label" :value="t.value"/>
            </mobile-select>
          </div>
        </data-input>
        <!-- 天数与时长类型都是推导值，不让申请人手填 -->
        <data-input label="请假时长">
          <span class="row-text" :class="{placeholder: derivedDays === null}">
            {{ derivedDays === null ? "填完起止时间后自动计算" : derivedDays + " 天 · " + derivedTypeName }}
          </span>
        </data-input>
        <data-input label="请假原因" v-model="editData.leaveReason" desc="" placeholder="请假必须写明原因"/>
        <data-input label="备注" v-model="editData.remark" desc="" placeholder="选填"/>
        <!-- 附件单独占一块（多张图，塞不进 data-input 的行里） -->
        <div class="attach-block">
          <div class="attach-label">附件</div>
          <mobile-image-upload v-model="editData.attachUrl" :limit="6" folder="leave"/>
        </div>
      </list-card>
    </div>

    <!-- 查看模式的底部：撤回 / 删除 / 查看审批（按状态决定显示哪些，一个都没有就不渲染） -->
    <div class="detail-footer" v-if="isView && hasActions">
      <mobile-button-group>
        <mobile-button v-if="canWithdraw" icon="element-ui_refresh-left" @click="withdrawLeave">撤回</mobile-button>
        <mobile-button v-if="canDelete" icon="element-ui_delete" @click="deleteLeave">删除</mobile-button>
        <mobile-button v-if="detail.approvalStatus" icon="element-ui_view" @click="openFlowLog">查看审批</mobile-button>
      </mobile-button-group>
    </div>

    <!-- 编辑模式的底部：取消 / 保存草稿 / 提交审批 -->
    <div class="detail-footer" v-if="!isView">
      <mobile-button-group>
        <mobile-button icon="element-ui_close" @click="cancelEdit">取消</mobile-button>
        <mobile-button icon="element-ui_document" @click="saveDraft">保存草稿</mobile-button>
        <mobile-button icon="element-ui_check" @click="submitLeave">提交审批</mobile-button>
      </mobile-button-group>
    </div>

    <!-- 审批流转记录抽屉 -->
    <mobile-drawer v-model="showFlowLog" title="审批流转记录" :show-title-btn="false">
      <div class="flow-log-drawer">
        <div v-if="!flowLogDetail" class="flow-empty">{{ flowEmptyText }}</div>
        <template v-else>
          <list-card label="流程概览" type="form">
            <data-column label="流程">{{ flowLogDetail.processName || "—" }}</data-column>
            <data-column label="发起人">{{ flowLogDetail.startUserName || "—" }}</data-column>
            <data-column label="发起时间">{{ dateTime(flowLogDetail.startTime) }}</data-column>
            <data-column label="当前节点">{{ flowLogDetail.currentNodeNames || "—" }}</data-column>
            <data-column label="审批状态">{{ flowLogDetail.instanceStatusName || "—" }}</data-column>
          </list-card>
          <list-card v-if="flowLogs.length" label="流转轨迹" type="form">
            <div class="flow-item" v-for="(log, index) in flowLogs" :key="index">
              <div class="flow-head">
                <span class="node">{{ log.nodeName || "流程" }}</span>
                <span class="type-tag" :class="'tag-' + actionTagType(log.action)">{{ log.actionName || "—" }}</span>
                <span class="time">{{ dateTime(log.createdTime) }}</span>
              </div>
              <div class="flow-operator">{{ log.operatorName || "系统" }}</div>
              <div v-if="log.opinion" class="flow-opinion">{{ log.opinion }}</div>
              <div v-if="log.evalRule" class="flow-rule">命中规则：{{ log.evalRule }}</div>
            </div>
          </list-card>
          <div v-else class="flow-empty">暂无流转记录</div>
        </template>
      </div>
    </mobile-drawer>
  </div>
</template>

<script>

import {useRoute, useRouter} from "vue-router";
import ListCard from "@/components/qywx/layout/ListCard.vue";
import DataInput from "@/components/qywx/data/DataInput.vue";
import DataColumn from "@/components/qywx/data/DataColumn.vue";
import MobileSelect from "@/components/qywx/data/MobileSelect.vue";
import MobileOption from "@/components/qywx/data/MobileOption.vue";
import MobileImageUpload from "@/components/qywx/data/MobileImageUpload.vue";
import MobileDateSelect from "@/components/qywx/data/MobileDateSelect.vue";
import MobileDrawer from "@/components/qywx/data/MobileDrawer.vue";
import MobileButtonGroup from "@/components/qywx/button/MobileButtonGroup.vue";
import MobileButton from "@/components/qywx/button/MobileButton.vue";
import leaveApplyApi from "@/api/dz-crm/leave/leaveApplyApi";
import commonApi from "@/api/commonApi";
import util from "@/util/util";
import {
  STATUS, TIME_TYPES, TYPE_COLORS,
  statusName, statusTagType, approvalName, approvalTagType,
  durationTypeName, deriveDays, deriveDurationType
} from "@/views/h5/crm/leaveDictionary";

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "LAD",
  components: {
    MobileButton, MobileButtonGroup, MobileDrawer, MobileImageUpload, MobileDateSelect, MobileOption, MobileSelect,
    DataColumn, DataInput, ListCard
  },
  data() {
    return {
      mode: "view",  // view（查看）/ edit（编辑，新建时也是它）
      detail: {},
      editData: {},
      typeList: [],
      departTree: [],
      timeTypes: TIME_TYPES,
      // 审批流转记录抽屉
      showFlowLog: false,
      flowLogDetail: null,
      flowEmptyText: "该单据尚未发起审批",
      pageTitle: "",
    };
  },
  computed: {
    /** 日期输入框的最小值：只允许今天及以后，避免补录历史假单（原生 input 的 min 只认 yyyy-MM-dd） */
    minDate() {
      const d = new Date();
      const pad = n => String(n).padStart(2, "0");
      return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
    },
    isView() {
      return this.mode === "view";
    },
    isDraft() {
      return Number(this.detail.leaveStatus) === STATUS.DRAFT;
    },
    // 撤回：只有审批中的单子能撤
    canWithdraw() {
      return Number(this.detail.leaveStatus) === STATUS.APPROVING;
    },
    // 删除：只有草稿能删
    canDelete() {
      return Number(this.detail.leaveStatus) === STATUS.DRAFT;
    },
    hasActions() {
      return this.canWithdraw || this.canDelete || !!this.detail.approvalStatus;
    },
    /**
     * 编辑按钮文案
     * 驳回/中止/撤回后申请状态都会回到草稿，与「从没提交过的新草稿」在申请状态上无从区分，
     * 靠审批状态才能看出它经历过一次审批 —— 那种情况该说「重新提交」。
     */
    editLabel() {
      const status = this.detail.approvalStatus;
      return (status === 500 || status === 200 || status === 400) ? "重新提交" : "编辑";
    },
    flowLogs() {
      return (this.flowLogDetail && this.flowLogDetail.logList) || [];
    },
    /** 推导的请假天数（null = 起止时间还没填全） */
    derivedDays() {
      return deriveDays(this.editData.startDate, this.editData.startTimeType,
          this.editData.endDate, this.editData.endTimeType);
    },
    /** 推导的时长类型名称，与天数配套展示 */
    derivedTypeName() {
      if (this.derivedDays === null) {
        return "";
      }
      const type = deriveDurationType(this.editData.startDate, this.editData.startTimeType,
          this.editData.endDate, this.editData.endTimeType);
      return durationTypeName(type);
    }
  },
  created() {
    this.pageTitle = document.title;
    document.title = this.route.query.id ? "请假申请详情" : "新增请假";
    // 请假类型是一致性字典（type = LEAVE_TYPE），取值 userCode / 显示 dictName
    commonApi.queryUniqueDictionaryList("LEAVE_TYPE").then(list => this.typeList = list || []);
    commonApi.queryDepartList().then(list => this.departTree = list || []);
    if (this.route.query.id) {
      // 点列表进来 = 查看模式
      this.mode = "view";
      this.findById();
    } else {
      // 新建 = 编辑模式
      this.mode = "edit";
      this.initNewLeave();
    }
  },
  unmounted() {
    document.title = this.pageTitle;
  },
  methods: {
    findById() {
      leaveApplyApi.findById(this.route.query.id).then(res => {
        this.detail = res || {};
      });
    },
    /**
     * 新增请假单
     *
     * ★ 新增时必须先向后端申请一个编码当 id —— 工作流拦截器要从 body 里
     *   取 biz_key_field(id) 的值去查流程实例，id 必须先于提交存在。
     *   单号 leaveNo 与 id 共用这个值，不再由后端另外生成。
     */
    async initNewLeave() {
      let id = "";
      try {
        id = await leaveApplyApi.applyCode();
      } catch (e) {
        this.$message.error("申请单号失败，请稍后重试");
        this.router.back();
        return;
      }
      const userInfo = await util.getUserInfo();
      this.editData = {
        id: id,
        leaveNo: id,
        leaveTypeId: "",
        // 默认带出当前登录人的主部门，允许改（代他人请假的情况）
        departCode: userInfo.mainDepartCode || "",
        startDate: "",
        startTimeType: "",
        endDate: "",
        endTimeType: "",
        leaveReason: "",
        remark: "",
        attachUrl: "",
      };
    },
    // 查看模式 -> 编辑模式
    toEdit() {
      this.editData = JSON.parse(JSON.stringify(this.detail));
      this.editData.startDate = this.dateValue(this.editData.startDate);
      this.editData.endDate = this.dateValue(this.editData.endDate);
      this.mode = "edit";
    },
    // 取消编辑：已有单据回查看模式（重新拉一次，丢弃未保存的改动）；新建则回列表
    cancelEdit() {
      if (this.route.query.id) {
        this.mode = "view";
        this.findById();
      } else {
        this.router.back();
      }
    },
    dateValue(value) {
      return value ? String(value).slice(0, 10) : "";
    },
    /**
     * 起止时间变化时，把结束日期默认跟随开始日期。
     * 只在结束日期还没填时跟随，已填的不要覆盖 —— 用户可能就是要请多天。
     */
    onTimeChange() {
      if (this.editData.startDate && !this.editData.endDate) {
        this.editData.endDate = this.editData.startDate;
      }
      // 结束早于开始时，把结束拉平到开始，避免出现负数天数
      if (this.editData.startDate && this.editData.endDate && this.derivedDays === null) {
        this.editData.endDate = this.editData.startDate;
        this.editData.endTimeType = this.editData.startTimeType;
      }
    },
    /** 组装提交给后端的表单：天数与时长类型都由前端推导后一并提交 */
    buildPayload(leaveStatus) {
      const payload = JSON.parse(JSON.stringify(this.editData));
      delete payload.applicantName;
      delete payload.departTitle;
      delete payload.leaveTypeTitle;
      delete payload.longLeave;
      payload.leaveDuration = this.derivedDays;
      payload.leaveDurationType = deriveDurationType(this.editData.startDate, this.editData.startTimeType,
          this.editData.endDate, this.editData.endTimeType);
      // ★ leaveStatus 必须随 body 一起传：它同时是工作流的 status_trigger，
      //   拦截器从 body 读它来决定是否发起流程，不传会读到 null 直接放行
      payload.leaveStatus = leaveStatus;
      return payload;
    },
    /**
     * 表单校验：必填 + 时长合法性
     * 保存草稿与提交共用同一套校验 —— 两条路径本该收同样的约束，
     * 各自写一遍迟早会出现「草稿能存、提交报错」这种分叉。
     */
    checkForm() {
      if (!this.editData.leaveTypeId) {
        this.$message.error("请选择请假类型");
        return false;
      }
      if (!this.editData.startDate || !this.editData.startTimeType) {
        this.$message.error("请选择开始日期和时段");
        return false;
      }
      if (!this.editData.endDate || !this.editData.endTimeType) {
        this.$message.error("请选择结束日期和时段");
        return false;
      }
      if (this.derivedDays === null) {
        this.$message.error("结束时间不能早于开始时间");
        return false;
      }
      if (!this.editData.leaveReason) {
        this.$message.error("请填写请假原因");
        return false;
      }
      return true;
    },
    // 保存草稿（状态由服务端强制置 0）
    saveDraft() {
      if (!this.checkForm()) {
        return;
      }
      // 成功提示由请求拦截器统一弹（saveDraft 是 save 接口）
      leaveApplyApi.saveDraft(this.buildPayload(STATUS.DRAFT)).then(() => this.afterSave());
    },
    // 提交：保存当前表单并直接进入审批流程
    submitLeave() {
      if (!this.checkForm()) {
        return;
      }
      this.$confirm("提交后进入审批流程，提交期间不可编辑，确认提交？", "提示", {type: "warning"})
          .then(() => leaveApplyApi.saveSubmit(this.buildPayload(STATUS.APPROVING)))
          .then(() => this.afterSave())
          .catch(() => {
          });
    },
    afterSave() {
      if (this.route.query.id) {
        // 编辑已有单据：回查看模式并刷新
        this.mode = "view";
        this.findById();
      } else {
        // 新建：回列表
        this.router.back();
      }
    },
    // ---------- 查看模式的三个动作 ----------
    /** 撤回：把审批中的单子撤回到草稿，可再编辑提交 */
    withdrawLeave() {
      this.$confirm("撤回后单据回到草稿，可修改后重新提交，确认撤回？", "提示", {type: "warning"})
          .then(() => leaveApplyApi.withdrawLeave(this.detail.id))
          .then(() => this.findById())
          .catch(() => {
          });
    },
    /** 删除：只有草稿能删 */
    deleteLeave() {
      this.$confirm("删除后不可恢复，确认删除？", "提示", {type: "warning"})
          .then(() => leaveApplyApi.delete(this.detail.id))
          .then(() => this.router.back())
          .catch(() => {
          });
    },
    /**
     * 查看本单据的审批流转记录
     * 按单据 id 反查流程实例 —— 业务页手里只有自己的单据 id，没有流程实例 id。
     */
    openFlowLog() {
      this.showFlowLog = true;
      this.flowLogDetail = null;
      this.flowEmptyText = "该单据尚未发起审批";
      leaveApplyApi.queryFlowInstance(this.detail.id).then(res => {
        this.flowLogDetail = res || null;
      }).catch(() => {
        this.flowEmptyText = "审批记录加载失败，请稍后重试";
      });
    },
    /** 轨迹动作标签色，与后端 ProcessFlowConstant.Action 对齐 */
    actionTagType(action) {
      const map = {
        100: "primary", 110: "primary", 200: "success", 210: "danger", 220: "warning",
        230: "warning", 240: "info", 300: "info", 400: "info", 500: "info", 600: "success", 700: "success",
        800: "info",
      };
      return map[action] || "info";
    },
    // ---------- 展示 ----------
    typeName(typeUserCode) {
      const type = this.typeList.find(item => item.userCode === typeUserCode);
      return type ? type.dictName : "";
    },
    typeTagType(typeUserCode) {
      const index = this.typeList.findIndex(item => item.userCode === typeUserCode);
      return index < 0 ? "info" : TYPE_COLORS[index % TYPE_COLORS.length];
    },
    statusName(status) {
      return statusName(status);
    },
    statusTagType(status) {
      return statusTagType(status);
    },
    approvalName(status) {
      return approvalName(status);
    },
    approvalTagType(status) {
      return approvalTagType(status);
    },
    durationTypeName(value) {
      return durationTypeName(value);
    },
    /** 起止时间展示：2026-10-08 上午 → 2026-10-09 下午 */
    dateRangeText(startDate, startTimeType, endDate, endTimeType) {
      const start = startDate ? String(startDate).slice(0, 10) : "—";
      const end = endDate ? String(endDate).slice(0, 10) : "—";
      const startSlot = this.timeTypes.find(t => t.value === startTimeType);
      const endSlot = this.timeTypes.find(t => t.value === endTimeType);
      return start + " " + (startSlot ? startSlot.label : "") + " → " + end + " " + (endSlot ? endSlot.label : "");
    },
    days(value) {
      if (value === null || value === undefined || value === "") {
        return "0";
      }
      return String(Number(value));
    },
    dateOnly(value) {
      return value ? String(value).slice(0, 10) : "—";
    },
    dateTime(value) {
      return value ? String(value).slice(0, 19) : "—";
    }
  }
};
</script>

<style lang="scss" scoped>
.leave-detail{
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--yq-background-color);
  .detail-body{
    flex: 1;
    overflow-y: auto;
    padding: 10px;
  }
  .detail-footer{
    flex: none;
    display: flex;
    gap: 10px;
    padding: 10px;
    background: #FFF;
    box-shadow: 0 -1px 4px var(--yq-shadow);
    :deep(.mobile-button){
      flex: 1;
    }
  }
  // 标题行右侧：类型/状态标签 + 编辑按钮（请假原因较长，允许折两行）
  :deep(.card-title){
    height: auto;
    min-height: 60px;
    .name{
      white-space: normal;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      line-height: 1.3;
    }
    .desc{
      display: flex;
      align-items: center;
      gap: 6px;
    }
  }
  .edit-btn{
    flex: none;
    padding: 2px 10px;
    border-radius: 4px;
    font-size: var(--yq-font-size-mini-mobile);
    color: var(--el-color-primary);
    background: var(--el-color-primary-light-9);
  }
  .type-tag,
  .status-tag{
    flex: none;
    padding: 2px 8px;
    border-radius: 4px;
    font-size: var(--yq-font-size-mini-mobile);
  }
  .tag-primary{
    color: var(--el-color-primary);
    background: var(--el-color-primary-light-9);
  }
  .tag-info{
    color: var(--el-color-info);
    background: var(--el-fill-color-light);
  }
  .tag-warning{
    color: var(--el-color-warning);
    background: var(--el-color-warning-light-9);
  }
  .tag-success{
    color: var(--el-color-success);
    background: var(--el-color-success-light-9);
  }
  .tag-danger{
    color: var(--el-color-danger);
    background: var(--el-color-danger-light-9);
  }
  .row-text{
    color: var(--yq-font-color-base);
    &.placeholder{
      color: var(--yq-font-color-desc);
    }
  }
  // 附件块（查看/编辑共用）
  .attach-block{
    padding: 10px 0 0;
    .attach-label{
      margin-bottom: 8px;
      color: var(--yq-font-color-desc);
    }
  }
  // 开始/结束时间：日期 + 时段同一行
  .datetime-row{
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
  }
  // 选择类控件放在 data-input 行里：去掉自带边框/箭头，字号跟随整行
  :deep(.row-select .select-area){
    width: auto;
    box-shadow: none;
    padding: 0;
    background: transparent;
    font-size: inherit;
    color: var(--yq-font-color-base);
    .value{
      flex: none;
      width: auto;
      max-width: 220px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      text-align: right;
      &.placeholder{
        color: var(--yq-font-color-desc);
      }
    }
    .arrow-down{
      display: none;
    }
  }
  // 时段那一列只占够用宽度
  :deep(.time-select){
    flex: none;
    .select-area .value{
      max-width: 60px;
    }
  }
  // 日期选择占满剩余宽度，时段那一列紧跟其后（组件根元素带父组件 scope，直接写类名即可）
  .datetime-date{
    flex: 1;
    min-width: 0;
  }
  // 审批流转记录
  .flow-log-drawer{
    height: 100%;
    overflow-y: auto;
    padding: 10px;
    .flow-item{
      padding: 10px 0;
      border-bottom: 1px solid var(--yq-border-color);
      &:first-child{
        padding-top: 0;
      }
      &:last-child{
        border-bottom: none;
        padding-bottom: 0;
      }
      .flow-head{
        display: flex;
        align-items: center;
        gap: 8px;
        .node{
          font-weight: 600;
        }
        .time{
          margin-left: auto;
          font-size: var(--yq-font-size-mini-mobile);
          color: var(--yq-font-color-desc);
        }
      }
      .flow-operator{
        margin-top: 4px;
        font-size: var(--yq-font-size-mini-mobile);
        color: var(--yq-font-color-desc);
      }
      .flow-opinion{
        margin-top: 4px;
        line-height: 1.6;
        white-space: pre-wrap;
        word-break: break-all;
      }
      .flow-rule{
        margin-top: 4px;
        font-size: var(--yq-font-size-mini-mobile);
        color: var(--yq-font-color-desc);
        word-break: break-all;
      }
    }
    .flow-empty{
      padding: 20px;
      text-align: center;
      color: var(--yq-font-color-desc);
    }
  }
}
</style>
